import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, Gavel, FileText, Clock, CheckCircle, AlertCircle,
  ChevronRight, Eye, Calendar, TrendingUp, ArrowRight,
  XCircle, HelpCircle, LogOut
} from 'lucide-react';

export default function MonCompteAcheteur() {
  const { API, user, logout } = useContext(AppContext);
  const navigate = useNavigate();
  const [inscriptions, setInscriptions] = useState([]);
  const [offres, setOffres] = useState([]);
  const [vacations, setVacations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/encheres/connexion');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      // Fetch my inscriptions (now includes vacation details)
      const inscResponse = await axios.get(`${API}/encheres/mes-inscriptions?acheteur_id=${user.id}`);
      const inscData = inscResponse.data || [];
      setInscriptions(inscData);

      // Build vacations map from inscription data
      const vacationMap = {};
      for (const insc of inscData) {
        if (insc.vacation_id && !vacationMap[insc.vacation_id]) {
          vacationMap[insc.vacation_id] = {
            id: insc.vacation_id,
            titre_fr: insc.vacation_titre,
            slug: insc.vacation_slug,
            statut: insc.vacation_statut,
            date_ouverture_vente: insc.vacation_date_ouverture,
            date_cloture_vente: insc.vacation_date_cloture
          };
        }
      }
      setVacations(vacationMap);

      // Fetch my offers (we'll need to add this endpoint)
      try {
        const offresResponse = await axios.get(`${API}/encheres/mes-offres?acheteur_id=${user.id}`);
        setOffres(offresResponse.data || []);
      } catch (e) {
        // Endpoint might not exist yet
        console.log('Mes offres endpoint not available yet');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (statut) => {
    const configs = {
      recue: { 
        label: 'Dossier reçu', 
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: Clock,
        description: 'Votre inscription a été reçue. Envoyez vos pièces justificatives.'
      },
      incomplete: { 
        label: 'Dossier incomplet', 
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        icon: AlertCircle,
        description: 'Des pièces sont manquantes. Consultez l\'email reçu.'
      },
      validee: { 
        label: 'Dossier validé', 
        color: 'bg-green-100 text-green-700 border-green-200',
        icon: CheckCircle,
        description: 'Vous pouvez enchérir sur les lots de cette vente.'
      },
      refusee: { 
        label: 'Dossier refusé', 
        color: 'bg-red-100 text-red-700 border-red-200',
        icon: XCircle,
        description: 'Votre inscription n\'a pas été retenue.'
      }
    };
    return configs[statut] || { label: statut, color: 'bg-gray-100 text-gray-700', icon: HelpCircle, description: '' };
  };

  const getVacationStatusConfig = (statut) => {
    const configs = {
      annoncee: { label: 'Inscriptions ouvertes', color: 'bg-green-500' },
      inscriptions_closes: { label: 'Inscriptions closes', color: 'bg-yellow-500' },
      vente_en_cours: { label: 'Enchères en cours', color: 'bg-[#F0A22E]' },
      close: { label: 'Enchères closes', color: 'bg-gray-500' },
      attribuee: { label: 'Terminée', color: 'bg-blue-500' }
    };
    return configs[statut] || { label: statut, color: 'bg-gray-400' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Step indicator for inscription process
  const renderStepIndicator = (inscription, vacation) => {
    const modeVisite = 'facultative'; // Default, would come from lot data
    const steps = modeVisite === 'obligatoire' 
      ? [
          { key: 'inscription', label: 'Inscription', done: true },
          { key: 'documents', label: 'Documents', done: inscription.statut !== 'recue' },
          { key: 'visite', label: 'Visite', done: inscription.visite_effectuee },
          { key: 'validation', label: 'Validation', done: inscription.statut === 'validee' }
        ]
      : [
          { key: 'inscription', label: 'Inscription', done: true },
          { key: 'documents', label: 'Documents', done: inscription.statut !== 'recue' },
          { key: 'validation', label: 'Validation', done: inscription.statut === 'validee' }
        ];

    return (
      <div className="flex items-center gap-2 mt-4">
        {steps.map((step, idx) => (
          <React.Fragment key={step.key}>
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
              step.done 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              {step.done ? (
                <CheckCircle className="w-3 h-3" />
              ) : (
                <span className="w-3 h-3 rounded-full border border-current" />
              )}
              {step.label}
            </div>
            {idx < steps.length - 1 && (
              <ChevronRight className="w-4 h-4 text-gray-300" />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F0A22E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Mon compte acheteur | GEOTS Enchères</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <div className="bg-[#0A2540] text-white">
        <div className="container mx-auto px-4 py-8">
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Mon compte</span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-500/20 p-3 rounded-lg">
                <User className="w-8 h-8 text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Mon espace acheteur</h1>
                <p className="text-gray-300">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-gray-600 text-gray-300 hover:bg-white/10"
              onClick={() => {
                logout();
                navigate('/encheres');
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="inscriptions" className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="inscriptions" className="data-[state=active]:bg-[#F0A22E] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Mes inscriptions
            </TabsTrigger>
            <TabsTrigger value="offres" className="data-[state=active]:bg-[#F0A22E] data-[state=active]:text-white">
              <Gavel className="w-4 h-4 mr-2" />
              Mes offres
            </TabsTrigger>
            <TabsTrigger value="historique" className="data-[state=active]:bg-[#F0A22E] data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" />
              Historique
            </TabsTrigger>
          </TabsList>

          {/* Inscriptions Tab */}
          <TabsContent value="inscriptions" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A2540]">Mes inscriptions aux ventes</h2>
              <Link to="/encheres">
                <Button>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Voir les ventes
                </Button>
              </Link>
            </div>

            {inscriptions.length === 0 ? (
              <Card className="bg-gray-50 border-dashed">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Aucune inscription
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Inscrivez-vous à une vente pour pouvoir enchérir sur les lots.
                  </p>
                  <Link to="/encheres">
                    <Button>Découvrir les ventes</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {inscriptions.map(inscription => {
                  const vacation = vacations[inscription.vacation_id];
                  const statusConfig = getStatusConfig(inscription.statut);
                  const StatusIcon = statusConfig.icon;
                  const vacationStatus = vacation ? getVacationStatusConfig(vacation.statut) : null;

                  return (
                    <Card key={inscription.id} className="overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-4">
                              <div className={`p-2 rounded-lg ${statusConfig.color}`}>
                                <StatusIcon className="w-5 h-5" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-bold text-[#0A2540]">
                                    {vacation?.titre_fr || 'Vente'}
                                  </h3>
                                  {vacationStatus && (
                                    <Badge className={`${vacationStatus.color} text-white text-xs`}>
                                      {vacationStatus.label}
                                    </Badge>
                                  )}
                                </div>
                                
                                <Badge variant="outline" className={`mt-2 ${statusConfig.color}`}>
                                  {statusConfig.label}
                                </Badge>
                                
                                <p className="text-sm text-gray-600 mt-2">
                                  {statusConfig.description}
                                </p>

                                {inscription.pseudonyme && inscription.statut === 'validee' && (
                                  <p className="text-sm mt-2">
                                    <span className="text-gray-500">Vous enchérissez en tant que : </span>
                                    <strong className="text-[#F0A22E]">{inscription.pseudonyme}</strong>
                                  </p>
                                )}

                                {renderStepIndicator(inscription, vacation)}
                              </div>
                            </div>
                          </div>

                          <div className="lg:text-right space-y-2">
                            <p className="text-sm text-gray-500">
                              Inscrit le {formatDate(inscription.created_at)}
                            </p>
                            {inscription.validated_at && (
                              <p className="text-sm text-green-600">
                                Validé le {formatDate(inscription.validated_at)}
                              </p>
                            )}
                            
                            {vacation && (
                              <Link to={`/encheres/${vacation.slug}`}>
                                <Button variant="outline" size="sm" className="mt-2">
                                  Voir la vente
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              </Link>
                            )}
                          </div>
                        </div>

                        {/* Documents reminder for pending inscriptions */}
                        {inscription.statut === 'recue' && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-medium text-blue-800 mb-2">
                              📋 Pièces à fournir par email
                            </h4>
                            <ul className="text-sm text-blue-700 space-y-1">
                              <li>• Extrait Kbis de moins de 3 mois</li>
                              <li>• Pièce d&apos;identité du dirigeant</li>
                              <li>• Justificatif de financement ou attestation bancaire</li>
                            </ul>
                            <p className="text-sm text-blue-600 mt-2">
                              Envoyez ces documents à <strong>contact@geots.consulting</strong>
                            </p>
                          </div>
                        )}

                        {/* Visit reminder */}
                        {inscription.statut === 'validee' && !inscription.visite_effectuee && (
                          <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="flex items-start gap-2">
                              <Eye className="w-5 h-5 text-orange-600 flex-shrink-0" />
                              <div>
                                <h4 className="font-medium text-orange-800">
                                  Visite non effectuée
                                </h4>
                                <p className="text-sm text-orange-700">
                                  Certains lots peuvent nécessiter une visite obligatoire avant d&apos;enchérir.
                                  Consultez les lots pour voir les créneaux disponibles.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Offres Tab */}
          <TabsContent value="offres" className="space-y-6">
            <h2 className="text-xl font-bold text-[#0A2540]">Mes offres en cours</h2>

            {offres.length === 0 ? (
              <Card className="bg-gray-50 border-dashed">
                <CardContent className="py-12 text-center">
                  <Gavel className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Aucune offre
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Vous n&apos;avez pas encore placé d&apos;offre sur les lots en vente.
                  </p>
                  <Link to="/encheres">
                    <Button>Voir les lots</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {offres.map(offre => (
                  <Card key={offre.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{offre.lot_titre || 'Lot'}</h3>
                          <p className="text-sm text-gray-500">
                            Offre placée le {formatDate(offre.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#F0A22E]">
                            {new Intl.NumberFormat('fr-FR').format(offre.montant)} €
                          </p>
                          {offre.montant_max_auto && (
                            <p className="text-xs text-gray-500">
                              Max auto: {new Intl.NumberFormat('fr-FR').format(offre.montant_max_auto)} €
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Historique Tab */}
          <TabsContent value="historique" className="space-y-6">
            <h2 className="text-xl font-bold text-[#0A2540]">Historique des ventes</h2>

            <Card className="bg-gray-50 border-dashed">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Historique vide
                </h3>
                <p className="text-gray-500">
                  Les ventes auxquelles vous avez participé apparaîtront ici.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Help Section */}
        <Card className="mt-8 bg-[#0A2540] text-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <HelpCircle className="w-8 h-8 text-[#F0A22E] flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2">Besoin d&apos;aide ?</h3>
                <p className="text-gray-300 mb-4">
                  Notre équipe est disponible pour répondre à vos questions sur les enchères.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="mailto:contact@geots.consulting" className="text-[#F0A22E] hover:underline">
                    contact@geots.consulting
                  </a>
                  <Link to="/encheres/conditions" className="text-gray-300 hover:text-white">
                    Consulter les conditions
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
